import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BrandDashboardLayout from "../../components/BrandDashboardLayout";
import { toast } from "sonner";
import { Plus, ArrowRight, Lock, ArrowLeft, X, Trash2 } from "lucide-react";
import { isBrandPro } from "../../lib/plan";

const STAGES = [
  { key: "target", label: "Target" },
  { key: "outreach", label: "Outreach" },
  { key: "replied", label: "Replied" },
  { key: "negotiating", label: "Negotiating" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

export function CampaignsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const isPro = isBrandPro(user);

  const load = () => {
    setLoading(true);
    api.get("/brand/campaigns").then(({ data }) => setCampaigns(data))
      .catch(e => { if (e.response?.status === 402) setCampaigns(null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (isPro) load(); else setLoading(false); }, [isPro]);

  if (!isPro) return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12 max-w-3xl">
        <div className="label-eyebrow">Brand Pro</div>
        <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="campaigns-title">Campaigns</h1>
        <div className="surface p-8 mt-8 border-l-2 border-volt" data-testid="campaigns-paywall">
          <Lock className="w-6 h-6 text-volt mb-4" />
          <div className="font-heading text-2xl uppercase">Campaign workspaces are Brand Pro</div>
          <p className="text-ink-muted text-sm mt-3 max-w-md">Group partnerships under a campaign. Move athletes through a Kanban pipeline. Track contract values. Export reports.</p>
          <Link to="/app/pricing" className="btn-volt inline-flex items-center gap-2 mt-6 text-sm" data-testid="campaigns-upgrade-btn">See Brand Pro <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </BrandDashboardLayout>
  );

  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="label-eyebrow">Workflow</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="campaigns-title">Campaigns</h1>
            <p className="text-ink-muted mt-2 text-sm">Group your partnership pipeline by campaign. Track each athlete from target through to deal.</p>
          </div>
          <button onClick={() => setShowNew(true)} className="btn-volt inline-flex items-center gap-2" data-testid="new-campaign-btn">
            <Plus className="w-4 h-4" /> New campaign
          </button>
        </div>

        {loading ? <div className="surface p-12 text-center text-ink-muted">Loading…</div> :
          campaigns.length === 0 ? (
            <div className="surface p-12 text-center" data-testid="campaigns-empty">
              <div className="font-heading text-2xl uppercase">No campaigns yet</div>
              <p className="text-ink-muted text-sm mt-2 mb-6">Create a campaign to start tracking partnerships in a pipeline.</p>
              <button onClick={() => setShowNew(true)} className="btn-volt inline-flex items-center gap-2 text-sm">
                <Plus className="w-4 h-4" /> Create your first campaign
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
              {campaigns.map(c => (
                <Link key={c.id} to={`/app/brand/campaigns/${c.id}`} className="surface p-5 hover:border-volt transition-colors group" data-testid={`campaign-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-heading text-lg uppercase tracking-tight truncate">{c.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-1">{c.status}</div>
                    </div>
                    {c.budget_total_gbp != null && <div className="text-volt font-mono text-lg shrink-0">£{c.budget_total_gbp.toLocaleString()}</div>}
                  </div>
                  {c.description && <p className="text-xs text-ink-muted mt-3 line-clamp-2">{c.description}</p>}
                  <div className="flex flex-wrap gap-2 mt-4 text-[9px] uppercase tracking-widest">
                    {STAGES.filter(s => c.partnership_counts[s.key] > 0).map(s => (
                      <span key={s.key} className="border border-line px-2 py-1 text-ink-muted">
                        {s.label}: <span className="text-white">{c.partnership_counts[s.key]}</span>
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-ink-muted mt-3">Total partnerships: <span className="text-white">{c.total_partnerships}</span></div>
                </Link>
              ))}
            </div>
          )}

        {showNew && <NewCampaignModal onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); navigate(`/app/brand/campaigns/${c.id}`); }} />}
      </div>
    </BrandDashboardLayout>
  );
}

function NewCampaignModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", budget_total_gbp: "", starts_on: "", ends_on: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Campaign name is required");
    setSaving(true);
    try {
      const body = { ...form };
      body.budget_total_gbp = body.budget_total_gbp ? parseInt(body.budget_total_gbp, 10) : null;
      const { data } = await api.post("/brand/campaigns", body);
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="surface w-full max-w-xl" onClick={e => e.stopPropagation()} data-testid="new-campaign-modal">
        <div className="p-6 border-b border-line flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Campaign</div>
            <h2 className="font-heading text-2xl uppercase tracking-tighter mt-2">New campaign</h2>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Name *"><Input value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testid="new-campaign-name" /></Field>
          <Field label="Description"><textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="new-campaign-description"
            className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm resize-none" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Budget £"><Input value={form.budget_total_gbp} onChange={v => setForm(f => ({ ...f, budget_total_gbp: v }))} testid="new-campaign-budget" placeholder="15000" /></Field>
            <Field label="Starts"><Input value={form.starts_on} onChange={v => setForm(f => ({ ...f, starts_on: v }))} placeholder="YYYY-MM-DD" testid="new-campaign-starts" /></Field>
            <Field label="Ends"><Input value={form.ends_on} onChange={v => setForm(f => ({ ...f, ends_on: v }))} placeholder="YYYY-MM-DD" testid="new-campaign-ends" /></Field>
          </div>
        </div>
        <div className="p-6 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line text-sm">Cancel</button>
          <button onClick={submit} disabled={saving || !form.name} className="btn-volt text-sm disabled:opacity-40" data-testid="new-campaign-submit">
            {saving ? "Creating…" : "Create campaign"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const isPro = isBrandPro(user);

  const load = () => {
    setLoading(true);
    api.get(`/brand/campaigns/${id}`).then(({ data }) => setData(data))
      .catch(() => toast.error("Campaign not found"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (isPro) load(); /* eslint-disable-next-line */ }, [id, isPro]);

  const moveStage = async (pid, stage) => {
    try {
      await api.patch(`/brand/campaigns/partnerships/${pid}`, { stage });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setValue = async (pid, value) => {
    try {
      await api.patch(`/brand/campaigns/partnerships/${pid}`, { contract_value_gbp: value });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removePartnership = async (pid) => {
    if (!window.confirm("Remove this partnership from the campaign?")) return;
    try { await api.delete(`/brand/campaigns/partnerships/${pid}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteCampaign = async () => {
    if (!window.confirm("Delete this campaign and all its partnerships? This cannot be undone.")) return;
    try { await api.delete(`/brand/campaigns/${id}`); toast.success("Campaign deleted"); navigate("/app/brand/campaigns"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!isPro) return <CampaignsList />;
  if (loading || !data) return <BrandDashboardLayout><div className="p-12 text-ink-muted">Loading…</div></BrandDashboardLayout>;

  const c = data.campaign;
  const parts = data.partnerships;

  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12">
        <button onClick={() => navigate("/app/brand/campaigns")} className="text-xs uppercase tracking-widest text-ink-muted hover:text-white inline-flex items-center gap-2 mb-6" data-testid="campaign-back-btn">
          <ArrowLeft className="w-3 h-3" /> Campaigns
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div className="min-w-0 flex-1">
            <div className="label-eyebrow">Campaign · {c.status}</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="campaign-name">{c.name}</h1>
            {c.description && <p className="text-ink-muted mt-3 text-sm max-w-2xl">{c.description}</p>}
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-ink-muted">
              {c.budget_total_gbp != null && <span>Budget: <span className="text-volt font-mono">£{c.budget_total_gbp.toLocaleString()}</span></span>}
              {c.starts_on && <span>Starts: <span className="text-white">{c.starts_on}</span></span>}
              {c.ends_on && <span>Ends: <span className="text-white">{c.ends_on}</span></span>}
            </div>
            {/* Collaborators strip */}
            <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="campaign-collaborators">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted">Team:</span>
              {(c.collaborators || []).map(u => (
                <span key={u.id} className="text-[10px] uppercase tracking-widest border border-line px-2 py-1" data-testid={`collaborator-${u.id}`}>
                  {u.full_name?.split(" ")[0] || u.email?.split("@")[0]} · <span className="text-volt">{u.role}</span>
                </span>
              ))}
              <Link to="/app/brand/organisation" className="text-[10px] text-volt uppercase tracking-widest hover:underline ml-1" data-testid="manage-team-link">+ Manage team</Link>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setShowAdd(true)} className="btn-volt text-sm inline-flex items-center gap-2" data-testid="add-partnership-btn">
              <Plus className="w-4 h-4" /> Add athlete
            </button>
            <button onClick={() => window.print()} className="btn-ghost-line text-sm" data-testid="campaign-print-btn">Print report</button>
            <button onClick={deleteCampaign} className="btn-ghost-line text-sm hover:text-[#FF3B30]" data-testid="campaign-delete-btn">Delete</button>
          </div>
        </div>

        {/* KANBAN */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="campaign-kanban">
          {STAGES.map(stage => {
            const items = parts.filter(p => p.stage === stage.key);
            return (
              <div key={stage.key} className="bg-bg-elev border border-line p-3 min-h-[300px]" data-testid={`kanban-col-${stage.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="label-eyebrow">{stage.label}</div>
                  <div className="text-xs text-ink-muted">{items.length}</div>
                </div>
                <div className="space-y-2">
                  {items.map(p => (
                    <div key={p.id} className="bg-bg border border-line p-3" data-testid={`kanban-card-${p.id}`}>
                      <div className="font-heading text-sm uppercase tracking-tight truncate">{p.athlete_display}</div>
                      <div className="text-[10px] text-ink-muted">{p.athlete_sport} · {p.athlete_location}</div>
                      {p.contract_value_gbp != null && (
                        <div className="text-[10px] text-volt font-mono mt-1">£{p.contract_value_gbp.toLocaleString()}</div>
                      )}
                      <div className="flex items-center justify-between mt-3 gap-2">
                        <select value={p.stage} onChange={e => moveStage(p.id, e.target.value)} data-testid={`kanban-stage-${p.id}`}
                          className="bg-bg-elev border border-line text-[10px] uppercase tracking-widest px-1.5 py-1 text-white">
                          {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                        <button onClick={() => removePartnership(p.id)} className="text-ink-muted hover:text-[#FF3B30]" data-testid={`kanban-remove-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                      {p.stage === "won" && p.contract_value_gbp == null && (
                        <button onClick={() => { const v = prompt("Contract value (£):"); if (v && !isNaN(parseInt(v,10))) setValue(p.id, parseInt(v,10)); }}
                          className="text-[9px] text-volt mt-2 uppercase tracking-widest" data-testid={`kanban-add-value-${p.id}`}>+ add value</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {showAdd && <AddAthleteModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} campaignId={id} />}
      </div>
    </BrandDashboardLayout>
  );
}

function AddAthleteModal({ onClose, onAdded, campaignId }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    api.get("/directory/athletes").then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);

  const add = async (aid) => {
    setAdding(aid);
    try {
      const { data } = await api.post(`/brand/campaigns/${campaignId}/partnerships`, { athlete_user_id: aid, stage: "target" });
      if (data.already) toast("Already in this campaign");
      else toast.success("Added to campaign");
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAdding(null); }
  };

  const filtered = q ? items.filter(a => a.full_name?.toLowerCase().includes(q.toLowerCase()) || a.sport?.toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="surface w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="add-partnership-modal">
        <div className="p-6 border-b border-line flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Add athlete</div>
            <h2 className="font-heading text-2xl uppercase tracking-tighter mt-2">Add a partnership</h2>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 border-b border-line">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or sport…" data-testid="add-partnership-search"
            className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-line">
          {filtered.slice(0, 60).map(a => (
            <div key={a.athlete_user_id} className="px-6 py-3 flex items-center justify-between gap-3" data-testid={`add-partnership-item-${a.athlete_user_id}`}>
              <div className="min-w-0">
                <div className="font-heading text-sm uppercase tracking-tight truncate">{a.full_name}</div>
                <div className="text-xs text-ink-muted truncate">{a.sport}{a.level && ` · ${a.level}`}{a.location && ` · ${a.location}`}</div>
              </div>
              <button onClick={() => add(a.athlete_user_id)} disabled={adding === a.athlete_user_id}
                className="btn-volt text-xs shrink-0 disabled:opacity-40" data-testid={`add-partnership-action-${a.athlete_user_id}`}>
                {adding === a.athlete_user_id ? "Adding…" : "Add"}
              </button>
            </div>
          ))}
          {filtered.length === 0 && <div className="px-6 py-10 text-center text-xs text-ink-muted">No athletes match. Try a different search.</div>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2 text-[10px]">{label}</label>{children}</div>; }
function Input({ value, onChange, placeholder = "", testid }) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />;
}
